// Timezone helpers.
//
// People book sessions across continents, so UI *must* render each wall-clock
// in the right timezone instead of just the viewer's local one. These helpers
// take an ISO instant (UTC) and format it in any IANA zone.

export const browserTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; }
  catch { return ''; }
};

// Human label for a tz: "Australia/Sydney" -> "Sydney". If `location` is set
// on the user profile (free text, e.g. "Sydney, AU") we prefer that.
export const tzLabel = (tz, location) => {
  if (location && location.trim()) return location.trim();
  if (!tz) return '';
  const parts = tz.split('/');
  return parts[parts.length - 1].replace(/_/g, ' ');
};

// Format an ISO moment in a given IANA zone. If `tz` is empty, falls back to
// the viewer's local zone (so legacy accounts without a timezone still work).
export const formatInTz = (iso, tz, opts = {}) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString('en-AU', {
      weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: tz || undefined,
      ...opts,
    });
  } catch {
    // Invalid tz string — fall back to viewer local.
    return d.toLocaleString('en-AU', {
      weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: true,
      ...opts,
    });
  }
};

// Short HH:MM in a given zone.
export const timeInTz = (iso, tz) =>
  formatInTz(iso, tz, { weekday: undefined, day: undefined, month: undefined });

// Convert a wall-clock in a given IANA zone (e.g. "2026-02-10", "14:30",
// "Australia/Sydney") into a UTC Date. We need this because <input type="date">
// + <input type="time"> only yield a *naive* wall-clock, which the browser
// otherwise interprets as the viewer's local zone — wrong when the booking
// form is phrased as "mentor's time".
export const wallClockToUtc = (dateStr, timeStr, tz) => {
  if (!dateStr || !timeStr) return null;
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = timeStr.split(':').map(Number);
  if (!y || !mo || !d || h == null || mi == null) return null;
  // Build a naive UTC guess for the wall-clock…
  const guess = Date.UTC(y, mo - 1, d, h, mi, 0);
  if (!tz) return new Date(guess);
  // …then measure the offset that zone reports for that instant, and
  // subtract it to land on the actual UTC moment.
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const parts = dtf.formatToParts(new Date(guess)).reduce((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = Number(p.value);
      return acc;
    }, {});
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    const offset = asUtc - guess; // how far ahead `tz` is vs UTC at that moment
    return new Date(guess - offset);
  } catch {
    return new Date(guess);
  }
};
